import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Styled Components
import {
  ActionButton,
  ButtonRow,
  PresetName,
  Container,
  ContainerLoader,
  ContainerText,
  ModalContainerStyled,
  Text,
  Title,
} from './styles';

// Types
import { ModalProps } from './types';

// Components
import { Loader } from '../../../inputs/Loader';

// Icons
import { AlertIcon } from '../../../../assets/icons/AlertIcon';

/**
 * Confirmación del borrado DURO de una plantilla de recorrido.
 *
 * Misma estructura que `DeleteExternalCompanyModal`: icono de alerta, bloque de
 * texto agrupado y fila de botones centrada.
 *
 * Aquí el borrado NO tiene condición de uso —ningún recorrido apunta a la
 * plantilla, porque el asistente copia su contenido en vez de referenciarla—,
 * así que nunca deja nada colgando. Se confirma igualmente porque no tiene
 * vuelta atrás: hay que volver a teclear las carreteras y los PK.
 */
export const DeleteRoutePresetModal = ({
  onClickClose,
  presetName,
  onConfirmDelete,
  isLoading: isLoadingProp,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'common']);
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(isLoadingProp || false);

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await onConfirmDelete();
      onClickClose();
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <ModalContainerStyled
      width={50}
      onClickClose={onClickClose}
      fill={theme.colors.black}
    >
      {isLoading && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      {/*
        role="dialog" + aria-modal: semántica correcta para un diálogo de
        confirmación (y anclaje estable para los E2E, que si no confunden este
        botón "Eliminar" con los de cada fila de la tabla).
      */}
      <Container
        {...props}
        role="dialog"
        aria-modal="true"
        aria-label={t('pages:management.deleteRoutePresetConfirmation')}
        isLoading={isLoading}
      >
        <AlertIcon />

        <ContainerText>
          <Title variant="semiBold">
            {t('pages:management.deleteRoutePresetConfirmation')}
          </Title>
          <PresetName variant="semiBold">{presetName}</PresetName>
          <Text size="M" colorText="greyDark">
            {t('pages:management.deleteRoutePresetWarning')}
          </Text>
        </ContainerText>

        <ButtonRow>
          <ActionButton
            type="button"
            text={t('common:buttons.cancel')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isLoading}
          />
          <ActionButton
            type="button"
            text={t('common:buttons.delete')}
            onClick={handleDelete}
            disabled={isLoading}
          />
        </ButtonRow>
      </Container>
    </ModalContainerStyled>
  );
};
